import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import './FavoritesPage.css';

/**
 * My Favorites — lists the properties the caller has marked with the heart
 * icon. Server-persisted (mirrors mobile FavoritesScreen.tsx), so the list is
 * identical across devices and sessions. Reachable from the header/quick-access
 * "Favorites" entries on the Properties page for every role.
 */
const FavoritesPage = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Firebase token helper — each page defines this locally (see CLAUDE.md)
  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  // Property field helpers — mirror PropertyDetailPage.js's flexible fallbacks,
  // since the single-property (mlsProperty) endpoint returns a flatter shape
  // than the search endpoint's nested mls/address/property structure.
  const getPropertyId = (prop) => {
    if (!prop) return '';
    return String(prop.mlsId || prop.mlsNumber || prop.listingId || prop.id || '');
  };

  const getPropertyAddress = (prop) => {
    if (!prop) return 'Unknown Address';
    return prop.address || prop.propertyAddress || prop.fullAddress || prop.name || 'Unknown Address';
  };

  const getPropertyLocation = (prop) => {
    if (!prop) return '';
    const city = prop.city || prop.propertyCity || '';
    const state = prop.state || prop.propertyState || '';
    const zip = prop.zipCode || prop.zip || prop.postalCode || prop.propertyZip || '';
    const parts = [city, state].filter(Boolean).join(', ');
    return zip ? `${parts} ${zip}` : parts;
  };

  const getPropertyPrice = (prop) => {
    if (!prop) return 'Price N/A';
    const price = prop.listPrice || prop.price || prop.askingPrice || prop.salePrice || 0;
    if (!price) return 'Price N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  const getPropertyBeds = (prop) => prop?.bedrooms || prop?.beds || prop?.bedroomCount || 0;
  const getPropertyBaths = (prop) => prop?.bathrooms || prop?.baths || prop?.bathroomCount || 0;
  const getPropertySqft = (prop) => prop?.squareFeet || prop?.sqft || prop?.livingArea || prop?.area || 0;

  const getPropertyDetails = (prop) => {
    const beds = getPropertyBeds(prop);
    const baths = getPropertyBaths(prop);
    const sqft = getPropertySqft(prop);
    const parts = [];
    if (beds) parts.push(`${beds} bed`);
    if (baths) parts.push(`${baths} bath`);
    if (sqft) parts.push(`${Number(sqft).toLocaleString()} sqft`);
    return parts.join(' • ') || 'Details N/A';
  };

  const getPropertyPhoto = (prop) => {
    if (!prop) return '';
    if (prop.photoUrl) return prop.photoUrl;
    if (prop.mainPhoto) return prop.mainPhoto;
    if (prop.imageUrl) return prop.imageUrl;
    if (prop.thumbnailUrl) return prop.thumbnailUrl;
    if (prop.primaryPhoto) return prop.primaryPhoto;
    if (Array.isArray(prop.photos) && prop.photos.length > 0) {
      const first = prop.photos[0];
      return typeof first === 'string' ? first : (first?.url || first?.href || '');
    }
    if (Array.isArray(prop.images) && prop.images.length > 0) {
      const first = prop.images[0];
      return typeof first === 'string' ? first : (first?.url || first?.href || '');
    }
    return '';
  };

  // Resolve each favorite ID to a full property via the MLS-detail endpoint,
  // in parallel. Missing/unavailable listings are silently dropped so a stale
  // favorite doesn't nuke the whole list (mirrors mobile loadFavoriteDetails).
  const loadFavoriteDetails = useCallback(async (ids, firebaseToken) => {
    if (!ids || ids.length === 0) {
      setProperties([]);
      return;
    }
    const results = await Promise.allSettled(
      ids.map((id) =>
        apiClient.get(API_ENDPOINTS.mlsProperty(id), {
          headers: { 'Authorization': `Bearer ${firebaseToken}` }
        })
      )
    );
    const resolved = results
      .filter((r) => r.status === 'fulfilled')
      .map((r) => {
        const data = r.value.data;
        if (Array.isArray(data)) return data[0];
        return data;
      })
      .filter(Boolean);
    setProperties(resolved);
  }, []);

  const loadFavorites = useCallback(async () => {
    try {
      const firebaseToken = await getFirebaseToken();
      const response = await apiClient.get(API_ENDPOINTS.favoritesIds, {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      });
      const ids = response.data?.propertyIds ?? [];
      await loadFavoriteDetails(ids, firebaseToken);
    } catch (error) {
      console.error('Error loading favorites:', error);
      setProperties([]);
    }
  }, [getFirebaseToken, loadFavoriteDetails]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await loadFavorites();
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadFavorites();
    setIsRefreshing(false);
  };

  // Un-hearting from this list removes the property immediately — no need
  // to refetch the whole list for a single removal.
  const handleUnfavorite = async (prop, e) => {
    e.stopPropagation();
    const propertyId = getPropertyId(prop);
    if (!propertyId) return;

    setProperties((prev) => prev.filter((p) => getPropertyId(p) !== propertyId));

    try {
      const firebaseToken = await getFirebaseToken();
      await apiClient.delete(API_ENDPOINTS.favoriteProperty(propertyId), {
        headers: { 'Authorization': `Bearer ${firebaseToken}` }
      }).catch((err) => {
        if (err?.response?.status !== 404) throw err;
      });
    } catch (error) {
      console.error('Error removing favorite:', error);
      // Roll back — re-add the property to the visible list
      setProperties((prev) => [...prev, prop]);
      alert('Could not update favorite. Please try again.');
    }
  };

  const handleViewProperty = (prop) => {
    const propertyId = getPropertyId(prop);
    if (propertyId) navigate(`/property/${propertyId}`);
  };

  return (
    <div className="fp-container">
      <div className="fp-layout">
        <div className="fp-content">
          {/* Back sits on its own row above the title — matches pd-back-button
              on the Property Detail page — so the title itself stays flush in
              the left corner like Transactions / Prospect Timeline. */}
          <button className="fp-back-btn" onClick={() => navigate('/properties')}>
            <span>←</span>
            <span>Back</span>
          </button>

          <div className="fp-header">
            <h1 className="fp-title">My Favorites</h1>
            <button
              className="fp-refresh-btn"
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              aria-label="Refresh favorites"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={isRefreshing ? 'fp-spin' : ''}>
                <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
            </button>
          </div>

          {isLoading ? (
            <div className="fp-loading">
              <div className="fp-loading-spinner"></div>
              <span>Loading your favorites...</span>
            </div>
          ) : properties.length > 0 ? (
            <div className="fp-grid">
              {properties.map((prop, index) => (
                <div className="fp-card" key={getPropertyId(prop) || index} onClick={() => handleViewProperty(prop)}>
                  <div className="fp-card-image">
                    {getPropertyPhoto(prop) ? (
                      <img src={getPropertyPhoto(prop)} alt={getPropertyAddress(prop)} className="fp-card-img" />
                    ) : (
                      <div className="fp-card-img-placeholder">🏠</div>
                    )}
                    <button
                      className="fp-heart-btn"
                      onClick={(e) => handleUnfavorite(prop, e)}
                      aria-label="Remove from favorites"
                    >
                      ♥
                    </button>
                  </div>
                  <div className="fp-card-info">
                    <div className="fp-card-price">{getPropertyPrice(prop)}</div>
                    <div className="fp-card-address">{getPropertyAddress(prop)}</div>
                    <div className="fp-card-location">{getPropertyLocation(prop)}</div>
                    <div className="fp-card-specs">{getPropertyDetails(prop)}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="fp-empty">
              <div className="fp-empty-icon">♡</div>
              <h3>No favorites yet</h3>
              <p>Tap the heart on any property to save it here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FavoritesPage;
